import Phaser from "phaser";
import { TRADE_PANORAMA_ART, TRADE_QUARTER_ART, TRADE_PANORAMAS } from "../config/tradeQuarter";
import { fitTradePanorama } from "./tradePanoramaLayout";

export type MarketSceneKind = "exchange" | "quests" | "liquidity" | "archive";
export type MarketRuntime = { destroy(): void; setKind(kind: MarketSceneKind): void };

/** The profile and destinations are camera views of the SAME connected district. */
export function createMarketGame(parent: HTMLElement, kind: MarketSceneKind, reducedMotion: boolean, tile = false): MarketRuntime {
  reducedMotion ||= window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  let current = kind;
  const markReady = () => { parent.dataset.marketSceneReady = "true"; };
  class MarketScene extends Phaser.Scene {
    district?: Phaser.GameObjects.Image;
    shine?: Phaser.GameObjects.Graphics;
    preload() {
      this.load.image("market-district", TRADE_PANORAMA_ART.map);
      // Warm the existing nameplate while the profile preview is displayed.
      if (tile) this.load.image("market-nameplate", TRADE_QUARTER_ART.plaque);
    }
    create() {
      if (!this.textures.exists("market-district")) return;
      this.district = this.add.image(0, 0, "market-district").setOrigin(0);
      this.shine = this.add.graphics().lineStyle(1.2 * ratio, 0xfff1ce, .85)
        .lineBetween(-3 * ratio, 0, 3 * ratio, 0).lineBetween(0, -3 * ratio, 0, 3 * ratio).setAlpha(0);
      this.layout();
      // Scene creation precedes its first drawn frame. Keep the native panorama
      // underneath, including across resize/restore, and report actual rendering.
      this.game.events.once(Phaser.Core.Events.POST_RENDER, markReady);
      this.scale.on("resize", () => this.layout());
      if (!reducedMotion) this.tweens.add({ targets: this.shine, alpha: .7, duration: 2000, ease: "Sine.InOut", yoyo: true, repeat: -1, repeatDelay: 2200 });
    }
    layout() {
      const w = this.scale.width, h = this.scale.height;
      const camera = TRADE_PANORAMAS[tile ? "preview" : current];
      const frame = fitTradePanorama(tile ? "preview" : current, w, h);
      this.district?.setPosition(frame.x, frame.y).setDisplaySize(frame.width, frame.height);
      this.shine?.setPosition(w * camera.lightX, h * camera.lightY);
    }
  }
  const scene = new MarketScene({ key: "KingdomMarket" });
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true"); canvas.style.pointerEvents = "none";
  const game = new Phaser.Game({ type: Phaser.CANVAS, canvas, parent,
    width: Math.max(1, Math.round(parent.clientWidth * ratio)), height: Math.max(1, Math.round(parent.clientHeight * ratio)),
    transparent: true, antialias: true, banner: false, audio: { noAudio: true },
    fps: { target: reducedMotion ? 15 : 30, forceSetTimeOut: true },
    render: { pixelArt: false, roundPixels: false, antialias: true },
    scale: { mode: Phaser.Scale.NONE }, scene: [scene] });
  const observer = new ResizeObserver(() => game.scale.resize(Math.max(1, Math.round(parent.clientWidth * ratio)), Math.max(1, Math.round(parent.clientHeight * ratio))));
  observer.observe(parent);
  const visibility = () => {
    if (document.hidden && scene.sys.isActive()) scene.scene.pause();
    else if (!document.hidden && scene.sys.isPaused()) scene.scene.resume();
  };
  document.addEventListener("visibilitychange", visibility);
  return {
    setKind(next) { current = next; if (scene.sys.isActive()) scene.layout(); },
    destroy() { observer.disconnect(); document.removeEventListener("visibilitychange", visibility); game.events.off(Phaser.Core.Events.POST_RENDER, markReady); game.destroy(true); canvas.remove(); delete parent.dataset.marketSceneReady; },
  };
}
